import { useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { LangGraphContext } from '@/Pages/Graph/src/api';
import { STUDIO_BASE_URL_LOCAL_STORAGE_KEY } from '@/Pages/Graph/src/constants';
import { useStudioMode } from '@/Pages/Graph/src/hooks/useStudioMode';
import { StudioRenderMode } from '@/Pages/Graph/src/hooks/useStudioRenderMode';
import { useOrganizationId } from '@/hooks/useSwr';
import { addQueryParams } from '@/utils/add-query-params';

import { useLocalStorageState } from './use-local-storage-state';

export const useGetGraphStudioPath = () => {
  const organizationId = useOrganizationId();
  const [lastStudioUrl, setLastStudioUrl] = useLocalStorageState(
    STUDIO_BASE_URL_LOCAL_STORAGE_KEY,
    undefined,
    z.string()
  );

  const { lastStudioMode } = useStudioMode();

  const [searchParams] = useSearchParams();
  let baseUrl: string | undefined;
  let hostProjectIdFromParams: string | undefined;
  const client = useContext(LangGraphContext);

  if (!client) {
    baseUrl = searchParams.get('baseUrl') ?? lastStudioUrl;
  } else {
    hostProjectIdFromParams = searchParams.get('hostProjectId') ?? undefined;
    baseUrl = client[1];
  }

  const mode = searchParams.get('mode') ?? lastStudioMode;

  const generatePath = useCallback(
    ({
      threadId,
      hostProjectId,
      assistantId,
      datasetId,
      render = StudioRenderMode.INTERACT,
    }: {
      threadId?: string;
      hostProjectId?: string;
      assistantId?: string;
      datasetId?: string;
      render?: StudioRenderMode;
    }) => {
      const hostProjectIdToUse = hostProjectId ?? hostProjectIdFromParams;
      const base = `/studio/thread`;
      return addQueryParams(base, {
        organizationId,
        render,
        ...(hostProjectIdToUse
          ? { hostProjectId: hostProjectIdToUse }
          : baseUrl
          ? {
              baseUrl,
            }
          : {}),
        ...(threadId ? { threadId } : {}),
        ...(assistantId ? { assistantId } : {}),
        ...(mode ? { mode } : {}),
        ...(datasetId ? { datasetId } : {}),
      });
    },
    [organizationId, baseUrl, mode, hostProjectIdFromParams]
  );
  return {
    generatePath,
    lastStudioUrl,
    setLastStudioUrl,
  };
};
